import React, { useState } from 'react';
import { Upload, Lock, Loader2, X, FileText } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from '../hooks/use-toast';
import api, { getToken, setToken } from '../lib/api';

export default function MaterialUploadDialog({ open, onOpenChange, onUploaded }) {
  const [authed, setAuthed] = useState(!!getToken());
  const [passcode, setPasscode] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const reset = () => {
    setTitle(''); setDescription(''); setFile(null); setPasscode(''); setProgress(0);
  };

  const signIn = async (e) => {
    e.preventDefault();
    if (!passcode.trim()) return;
    try {
      const { token } = await api.login(passcode.trim());
      setToken(token); setAuthed(true);
      toast({ title: 'Signed in', description: 'You may now upload material.' });
    } catch {
      toast({ title: 'Incorrect passcode' });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title || !file) { toast({ title: 'Title and file are required' }); return; }
    setBusy(true); setProgress(0);
    try {
      const fd = new FormData();
      fd.append('title', title);
      fd.append('description', description || '');
      fd.append('file', file);
      await api.createMaterial(fd, (evt) => {
        if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
      });
      toast({ title: 'Material published', description: 'It is now in the Archive and Materials library.' });
      reset();
      onUploaded && onUploaded();
      onOpenChange(false);
    } catch (err) {
      toast({ title: 'Upload failed', description: String(err?.response?.data?.detail || err.message) });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="sm:max-w-lg bg-white">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl flex items-center gap-2">
            <FileText className="h-5 w-5 text-teal-deep" /> Upload Written Material
          </DialogTitle>
        </DialogHeader>

        {!authed ? (
          <form onSubmit={signIn} className="space-y-4 mt-2">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-cream/60">
              <Lock className="h-4 w-4 text-teal-deep" />
              <p className="text-sm text-ink/70">Uploads are protected. Enter the ministry passcode to continue.</p>
            </div>
            <div>
              <Label htmlFor="pc">Passcode</Label>
              <Input id="pc" type="password" value={passcode} onChange={e => setPasscode(e.target.value)} placeholder="Enter passcode" autoFocus />
            </div>
            <Button type="submit" className="w-full bg-teal-brand hover:bg-teal-deep text-white h-11">Continue</Button>
          </form>
        ) : (
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div>
              <Label htmlFor="mt">Title *</Label>
              <Input id="mt" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Notes on Romans 8" />
            </div>
            <div>
              <Label htmlFor="md">Description</Label>
              <Textarea id="md" rows={3} value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional short summary" />
            </div>
            <div>
              <Label htmlFor="mf">File *</Label>
              <Input id="mf" type="file" accept=".pdf,.doc,.docx,.txt,application/pdf" onChange={e => setFile(e.target.files?.[0] || null)} />
              {file && <p className="text-xs text-ink/50 mt-1">{file.name}</p>}
            </div>
            {busy && (
              <div className="p-3 rounded-lg bg-cream/60 border border-teal-brand/20">
                <div className="flex items-center gap-2 text-sm text-ink">
                  <Loader2 className="h-4 w-4 animate-spin text-teal-deep" /> Uploading… {progress}%
                </div>
                <div className="mt-2 h-1.5 rounded-full bg-white overflow-hidden">
                  <div className="h-full bg-gold-brand transition-all" style={{ width: `${progress}%` }} />
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <Button type="submit" disabled={busy} className="flex-1 bg-gold-brand hover:bg-[#C48A1F] text-ink font-semibold h-11">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />} Publish to Archive
              </Button>
              <Button type="button" variant="outline" onClick={() => { setToken(null); setAuthed(false); }} className="h-11" title="Sign out">
                <Lock className="h-4 w-4" />
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
